import { useSelector } from "hooks/useSelector";
import React, { useMemo } from "react";
import { Button, Card, CardBody, CardTitle, Table } from "reactstrap";
import { RootState } from "slices";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import { useCreateOrder, useUpdateOrder } from "hooks/useOrders";
import { useDispatch } from "react-redux";
import { clearCart, clearCustomerId, clearOrderId } from "slices/cart/reducer";
import { clearPayment } from "slices/payment/reducer";
import { OrderCreation } from "types";
import { useNavigate } from "react-router-dom";
import { OrderCreationValidationSchema } from "utils/rules";
import { BaseButton } from "Components";

const ConfirmationDetails = ({
  setActiveTab,
  order,
}: {
  setActiveTab: any;
  order?: any;
}) => {
  const navigate = useNavigate();
  const paymentDetails = useSelector((state: RootState) => state.payment);
  const cartItems = useSelector((state: RootState) => state.cart.items);
  console.log(cartItems);

  const currentBranch = useSelector(
    (state: RootState) => state.branch.currentBranch
  );
  const defaultCurrency = useSelector(
    (state: any) => state.Login.default_currency
  );

  // TODO: Get userId and customerId
  const customerId = useSelector((state: RootState) => state.cart.customer_id);

  const dispatch = useDispatch();
  const { mutate: createOrder } = useCreateOrder();
  const { mutate: updateOrder } = useUpdateOrder();

  const subtotal = useMemo(
    () =>
      cartItems.reduce((total, item) => total + item.price * item.quantity, 0),
    [cartItems]
  );

  const discount = useMemo(
    () =>
      cartItems.reduce((total, item) => {
        const amountDiscount =
          item.discount_amt > 0 ? Number(item.discount_amt) : 0;
        const percentageDiscount =
          item.discount_percentage > 0
            ? item.price * item.quantity * (item.discount_percentage / 100)
            : 0;
        return total + amountDiscount + percentageDiscount;
      }, 0),
    [cartItems]
  );

  // const calculateTax = () => {
  //   return cartItems.reduce((total, item) => {
  //     // Assuming item.allowTax is a boolean indicating if tax should be applied
  //     return total + (item.allowTax ? item.price * item.quantity * 0.15 : 0);
  //   }, 0);
  // };
  const tax = 0; //useMemo(() => subtotal * 0.15, [subtotal]);
  //const tax = useMemo(() => calculateTax(), [cartItems]);
  const amountPaid = paymentDetails?.amountPaid || 0;
  const balancePaid = paymentDetails?.balanceDue || 0;

  // Calculate the total amount (subtotal + tax - discount)
  const totalAmount = subtotal - discount;

  // Calculate balance due
  const balanceDue = amountPaid > totalAmount ? amountPaid - totalAmount : 0;
  console.log(paymentDetails);
  console.log(amountPaid);
  console.log(balancePaid);

  const formik = useFormik({
    initialValues: {
      paymentDetails,
      cartItems,
      subtotal,
      amountPaid,
      balanceDue,
      tax,
      status: "draft",
      customer_id: customerId,
    },
    enableReinitialize: true,
    validationSchema: OrderCreationValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!currentBranch?.id) {
        toast.error("Branch information is missing");
        setSubmitting(false);
        return;
      }

      const totalAmount = subtotal + tax;

      // Create an object that matches the OrderCreation type
      const orderData: OrderCreation = {
        total_amount: totalAmount,
        status: values.status,
        branch_id: Number(currentBranch.id),
        payment_method_id: paymentDetails.methodId,
        customer_id: Number(customerId),
        amount_paid: Number(amountPaid),
        balance_due: Number(balanceDue),
        order_items: cartItems.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
          price: item.price,
          discount_percentage: item.discount_percentage,
          discount_amt: item.discount_amt,
          round_up: 0,
        })),
      };

      const formData = new FormData();

      // Append values from orderData to formData
      Object.entries(orderData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              formData.append(
                `order_items[${index}][${itemKey}]`,
                String(itemValue)
              );
            });
          });
        } else {
          formData.append(key, String(value));
        }
      });

      console.log("Order: ", order);

      if (order) {
        updateOrder(
          { id: order.id, data: orderData },
          {
            onSuccess: (response) => {
              toast.success("Order updated successfully");
              dispatch(clearCart());
              dispatch(clearPayment());
              dispatch(clearCustomerId());
              dispatch(clearOrderId());
              navigate("/pos");
              setActiveTab("1");
            },
          }
        );
      } else {
        // await createOrderMutation.mutateAsync(formData);
        createOrder(formData, {
          onSuccess: () => {
            // Clear cart and payment from redux
            dispatch(clearCart());
            dispatch(clearPayment());
            dispatch(clearCustomerId());
            dispatch(clearOrderId());
            setActiveTab("1");
          },
          onError: () => {
            setSubmitting(false);
          },
        });
      }
    },
  });

  const renderPaymentMethodDetails = () => {
    if (!paymentDetails.method) return null;
    console.log(balanceDue);
    return (
      <>
        <p>
          <strong>Payment Method:</strong>
          <span className="badge bg-primary ms-2">
            {paymentDetails.method.charAt(0).toUpperCase() +
              paymentDetails.method.slice(1)}
          </span>
        </p>
      </>
    );
  };

  return (
    <Card className="shadow-none">
      <CardBody className="p-4">
        <CardTitle tag="h4" className="mb-4 text-primary">
          Order Confirmation
        </CardTitle>

        <form onSubmit={formik.handleSubmit}>
          <div className="cart-items mb-4">
            <h5 className="text-dark mb-3 border-bottom pb-2">Cart Items</h5>
            <Table className="table-borderless mb-0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="text-center">Quantity</th>
                  <th className="text-center">Price ({defaultCurrency})</th>
                  <th className="text-center">Discount ({defaultCurrency})</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item?.name}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-center  ">
                      {(item.price * item.quantity).toFixed(2)}
                    </td>
                    <td className="text-center">
                      {item.discount_amt && item.discount_amt > 0
                        ? item.discount_amt.toFixed(2)
                        : item.discount_percentage > 0
                        ? (
                            item.price *
                            item.quantity *
                            (item.discount_percentage / 100)
                          ).toFixed(2)
                        : "0.00"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="payment-details mb-4">
            <h5 className="text-dark mb-3 border-bottom pb-2">
              Payment Details
            </h5>
            <div className="ms-2">{renderPaymentMethodDetails()}</div>
          </div>

          <div className="order-summary">
            <h5 className="text-dark mb-3 border-bottom pb-2">Order Summary</h5>
            <Table className="table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Sub Total:</td>
                  <td className="text-end fw-bold">
                    {defaultCurrency} {formik.values.subtotal.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Discount:</td>
                  <td className="text-end fw-bold">
                    - {defaultCurrency} {discount.toFixed(2)}
                  </td>
                </tr>

                {/* <tr>
                <td className="text-muted">Tax (15%):</td>                
                  <td className="text-end fw-bold">
                    {defaultCurrency} {tax.toFixed(2)}                   
                  </td>                 
                </tr> */}
                <tr className="border-top">
                  <th className="h5">Total:</th>
                  <th className="text-end h5 text-primary">
                    {defaultCurrency} {(subtotal + tax - discount).toFixed(2)}
                  </th>
                </tr>
                {/* Add Amount Paid row */}
                <tr>
                  <td className="text-muted">Amount Paid:</td>
                  <td className="text-end fw-bold">
                    {defaultCurrency} {Number(amountPaid).toFixed(2)}
                  </td>
                </tr>
                {/* Add Balance/Change Due row */}
                <tr>
                  <td className="text-muted">Change Due:</td>
                  <td className="text-end fw-bold">
                    {defaultCurrency} {balanceDue.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </Table>

            <div className="d-flex justify-content-between mt-4">
              <Button color="dark" onClick={() => setActiveTab("1")}>
                Back
              </Button>
              <div className="d-flex justify-content-between gap-4">
                <BaseButton
                  type="button"
                  className="btn btn-warning btn-lg"
                  disabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                  onClick={() => {
                    if (!formik.values.customer_id) {
                      toast.error("Customer ID is required before proceeding.");
                      return;
                    }
                    console.log(formik.values);
                    formik.handleSubmit();
                  }}
                >
                  Draft Order
                </BaseButton>
                {/* TODO: Add loading effect for buttons */}
                <BaseButton
                  type="button"
                  className="btn btn-primary btn-lg"
                  disabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                  onClick={async () => {
                    await formik.setFieldValue("status", "approved");
                    if (!formik.values.customer_id) {
                      toast.error("Customer ID is required before proceeding.");
                      return;
                    }
                    formik.handleSubmit();
                  }}
                >
                  Pay now
                </BaseButton>
              </div>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default ConfirmationDetails;
